import {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {SingleDatePicker} from 'react-dates';

import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Button from "@mui/material/Button";
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import 'react-dates/lib/css/_datepicker.css';
import 'react-dates/initialize';

import NewSessionButton from '../sessions/NewSessionButton';
import CustomTimePicker from "./CustomTimePicker";
import TitleDivider from "../patientSignup/TitleDivider";
import FormLayout from "../patientSignup/FormLayout";
import './SelectADate.scss';
import FindCaseInput from '../cases/FindCaseInput';
import SchedulerPatientInfo from './SchedulerPatientInfo';

const handleDatePick = (setDate, onDateChange) => date => {
  setDate(date);
  onDateChange(date);
};

const isDayBlocked = dates => dateObj => (
  !dateObj
  ? false
  : !dates.includes(dateObj.format('MM-DD-yyyy'))
);

const handleTimeChange = (setSelectedTime, onTimeChange) => (time) => {
  setSelectedTime(time);
  onTimeChange(time)
}

const SelectDateForm = props => {
  const [date, setDate] = useState(props.date ? moment(props.date) : null);
  const [selectedTime, setSelectedTime] = useState(props.selectedTime || null);
  const {
    dates,
    selectedProvider,
    isProvider,
    onSubmit,
    isLoading,
    onDateChange,
    onTimeChange,
    timezone,
  } = props;
  if (!selectedProvider && !isProvider) return <CircularProgress/>;
  const timezoneInfo = moment().tz(
    timezone
    || Intl.DateTimeFormat().resolvedOptions().timeZone
  );
  return (
      <Stack
        className='SelectDateForm'
        direction='column'
        alignSelf='center'
      >
        {isLoading && (
          <Stack
            direction='row'
            justifyContent='center'
            spacing={2}
            style={{minHeight: '2.2em'}}
          >
            <Box style={{marginTop: '0.75em'}}>
              <Stack direction='row' spacing={1}>
                <CircularProgress size={24}/>
                <Typography variant='body2'>
                  Loading Availability
                </Typography>
              </Stack>
            </Box>
          </Stack>
        )}

        {/* Date */}
        <Stack className="date-form">
          {props?.isProvider && <SchedulerPatientInfo patient={props?.selectedPatient} />}
          <TitleDivider text='Select a Day' textStyle={{padding: '0'}}/>
          {
            props?.sessionType?.title 
            && <Typography variant='body' color='text.gray'>
              {props.sessionType.title}
            </Typography>
          }
          <Stack
            className="date-form__date-picker-wrapper"
            direction='row'
            justifyContent='center'
            alignItems='center'
          >
            <SingleDatePicker
              id='datePicker'
              date={date}
              focused={true}
              numberOfMonths={1}
              hideKeyboardShortcutsPanel={true}
              noBorder={true}
              minDate={moment()}
              daySize={40}
              isDayBlocked={isLoading ? () => true : isDayBlocked(dates)}
              onDateChange={handleDatePick(setDate, onDateChange)}
              onFocusChange={() => true}
              onPrevMonthClick={props.onPrevMonthClick}
              onNextMonthClick={props.onNextMonthClick}
            />
          </Stack>
        </Stack>

        {/* Time */}
        <Stack gap='12px' marginTop='30px !important'>
          <TitleDivider 
            text={`Choose a Time (${timezoneInfo.format('z')} Timezone)`}
            textStyle={{padding: '0'}}
          />

          {
            date && timezone && (
              <CustomTimePicker {...props}
                timezone={timezone}
                onDateChange={(date) => {
                  setDate(date);
                  onDateChange(date);
                }}
                onTimeChange={handleTimeChange(setSelectedTime, onTimeChange)}
              />
            )
          }
        </Stack>
        {/* Case Assignment */}
        {isProvider && props?.selectedPatient?.id &&
          <Stack gap='12px' marginTop='30px !important'>
            <TitleDivider 
              text='Attach a Case'
              textStyle={{padding: '0'}}
            />
            <FindCaseInput 
              patientId={props?.selectedPatient?.id}
              allowEdit={true}
              allowCreate={true}
              defaultValue={props.defaultCaseId}
              onSubmit={(e, {id}) => props.onCaseChange(e, {id})}
              onChange={(e, {id}) => props.onCaseChange(e, {id})}
              includeNonOwned={true}
            />
          </Stack>
        }
        {
          props.sessionType && 
          props.selectedPatient &&
          isProvider &&
          <NewSessionButton 
            sessionType={props?.sessionType}
            patient={props?.selectedPatient}
          />
        }
        <Button
          sx={{alignSelf: 'center', width: '225px', marginTop: '9px'}}
          variant='primaryGradient'
          fullWidth={false}
          size='medium'
          disabled={!date || !selectedTime}
          onClick={() => onSubmit(date, selectedTime)}
        >
          Schedule
        </Button>
        {
          props.sessionType &&
          props.selectedPatient &&
          isProvider &&
          <Button
            sx={{alignSelf: 'center', width: '225px', marginTop: '9px'}}
            variant='contained'
            color='success'
            fullWidth={false}
            size='medium'
            disabled={!date || !selectedTime}
            onClick={() => onSubmit(date, selectedTime, {resetOnSuccess: true})}
          >
            Save & Schedule More
          </Button>        }
        {
          props.hideManualScheduling
            ? null
            : <Typography variant='body1' marginTop={1}>
              Not seeing your preferred date or time?
              <Button onClick={props.onScheduleManually} endIcon={<ChevronRightIcon/>}>
                <Typography variant='body1'>Click here</Typography>
              </Button>
            </Typography>
        }
      </Stack>
  );
};

SelectDateForm.propTypes = {
  selectedProvider: PropTypes.object,
  sessionType: PropTypes.string, // key/id of the session type
  onDateChange: PropTypes.func,
  onCaseChange: PropTypes.func,
  onScheduleManually: PropTypes.func,
  onBack: PropTypes.func,
  defaultCaseId: PropTypes.string,
};

export default SelectDateForm;
